import imageCompression from "browser-image-compression";
import type { UploadMetadata } from "firebase/storage";
import {
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytes,
} from "firebase/storage";
import { storage } from "../auth/firebase";
import { sortMediaBrowserFiles } from "senf-atomic-design-system/src/components/special/imageModifier/MediaBrowser";
import {
  addFilesToCacheRaw,
  getFileCache,
} from "senf-atomic-design-system/src/hooks/useFileCache";

// Define MIME type to standard extension mapping outside the function for clarity
const mimeToExtension: Record<string, string> = {
  "image/webp": ".webp",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/svg+xml": ".svg",
  "video/mp4": ".mp4",
  "video/webm": ".webm",
  "video/avi": ".avi",
  "video/mpeg": ".mpeg",
  "audio/mpeg": ".mp3",
  "audio/wav": ".wav",
  "video/quicktime": ".mov",
  "text/csv": ".csv",
  "text/plain": ".txt",
  "application/pdf": ".pdf",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/geo+json": ".geojson",
  "application/zip": ".zip",
  // Add other mappings as needed
};

// Extend the compression options type to include target file type for images
type ExtendedCompressionOptions = Parameters<typeof imageCompression>[1] & {
  targetFileType?: string;
};

/**
 *
 * Uploads a file to firestore, handling compression, standardization, and duplicate naming.
 * @constructor
 * @param file - Instace of Javascript's File (new File).
 * @param path - The base file path in firestore (without the filename).
 * @param compressionOptions - Object containing compression options, including optional targetFileType for images.
 * @returns An object containing the { url, name, type } of the uploaded file.
 */

export const handleFileUpload = async (
  file: File,
  basePath: string,
  options?: {
    metadata?: UploadMetadata;
    compressionOptions?: ExtendedCompressionOptions;
  },
): Promise<{
  url: string;
  name: string;
  type: string;
  customMetadata?: Record<string, string>;
}> => {
  let fileToUpload = file;
  let actualFileType = file.type; // Start with original type
  const originalName = file.name;
  const lastDotIndex = originalName.lastIndexOf(".");
  const baseName =
    lastDotIndex >= 0 ? originalName.substring(0, lastDotIndex) : originalName;
  const originalExtension =
    lastDotIndex >= 0 ? originalName.substring(lastDotIndex) : "";

  // Apply compression if it's an image and compression options are provided
  if (file.type.startsWith("image/") && options?.compressionOptions) {
    const imageFile = new File([file], file.name, {
      type: file.type,
      lastModified: Date.now(),
    });

    try {
      fileToUpload = await imageCompression(
        imageFile,
        options?.compressionOptions,
      );
    } catch (error) {
      console.error(error);
    }
    // Update actual file type if conversion occurred
    actualFileType =
      options?.compressionOptions.targetFileType || fileToUpload.type;
  }

  // --- Check for existing file and rename if necessary ---

  // Determine target extension based on actual type after potential conversion
  const targetExtension = mimeToExtension[actualFileType] || originalExtension;
  const standardizedName = `${baseName}${targetExtension}`;

  let uploadName = standardizedName; // Start with the standardized name
  let uploadPath = `${basePath}/${uploadName}`;
  let fileStorageRef = ref(storage, uploadPath);
  let index = 1;
  const MAX_ATTEMPTS = 100; // Set a reasonable limit
  let attempts = 0;

  // Loop to find a unique name
  while (attempts < MAX_ATTEMPTS) {
    attempts++; // Increment attempts counter
    try {
      await getMetadata(fileStorageRef);
      // File exists, generate new name with index
      uploadName = `${baseName} (${index})${targetExtension}`; // Use baseName and targetExtension
      uploadPath = `${basePath}/${uploadName}`;
      fileStorageRef = ref(storage, uploadPath);
      index++;
    } catch (error: unknown) {
      // Type assertion to safely access error properties
      const hasCode = (
        err: unknown,
      ): err is { code: string; [key: string]: unknown } =>
        typeof err === "object" && err !== null && "code" in err;

      if (hasCode(error) && error.code === "storage/object-not-found") {
        // Path is available, break the loop
        break;
      }
      // Handle other potential errors (e.g., permissions)
      console.error("Error checking file metadata:", error);
      throw error; // Re-throw the error to stop the upload process
    }
  }
  // --- End check ---

  // If loop finished without breaking, throw an error
  if (attempts >= MAX_ATTEMPTS) {
    throw new Error(
      `Could not find a unique filename for "${baseName}${targetExtension}" after ${MAX_ATTEMPTS} attempts.`,
    );
  }

  await uploadBytes(fileStorageRef, fileToUpload, options?.metadata);

  // Get the file URL and return details
  const fileURL = await getDownloadURL(fileStorageRef);
  return {
    url: fileURL,
    name: uploadName,
    type: actualFileType,
    customMetadata: options?.metadata?.customMetadata,
  };
};

type MediaObjectProps = {
  fileList: File[];
  setPath: (file: File) => string;
  setName?: (file: File) => string;
  basePath?: string;
  options?: {
    metadata?: UploadMetadata;
    compressionOptions?: ExtendedCompressionOptions;
    pdfOptions?: Record<string, unknown>;
    addToCache?: boolean;
  };
};

export const getMediaObjects = ({
  fileList,
  setName,
  setPath,
  basePath,
  options,
}: MediaObjectProps): Promise<
  {
    url: string;
    name: string;
    path: string;
    size: number;
    width?: number;
    height?: number;
  }[]
> => {
  const fileCache = options?.addToCache !== false ? getFileCache() : {};
  let overlappingKey = basePath;

  const processFile = async (_file: File) => {
    const file = setName
      ? new File([_file], setName(_file), { type: _file.type })
      : _file;

    const path = setPath(file);

    // Check for existing cached files using the base path
    if (options?.addToCache !== false) {
      const overlap = Object.keys(fileCache).find((key) =>
        path.startsWith(key),
      );
      if (overlap) overlappingKey = overlap;
    }

    if (file.type.startsWith("image/")) {
      // Process images concurrently
      const imageMetaPromise = new Promise<HTMLImageElement>((resolve) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.src = URL.createObjectURL(file); // Use original file for metadata reading
      });

      const uploadPromise = handleFileUpload(file, path, {
        metadata: {
          ...options?.metadata,
          customMetadata: {
            ...(options?.metadata?.customMetadata || {}),
            lang: options?.metadata?.customMetadata?.lang,
          } as unknown as Record<string, string>,
        },
        compressionOptions: {
          maxSizeMB: options?.compressionOptions?.maxSizeMB || 1,
          maxWidthOrHeight:
            options?.compressionOptions?.maxWidthOrHeight || 1920,
          targetFileType:
            options?.compressionOptions?.targetFileType || "image/webp",
          useWebWorker: true,
        },
      });

      const [uploadResult, imageMeta] = await Promise.all([
        uploadPromise,
        imageMetaPromise,
      ]);

      return {
        id: `${path}/${uploadResult.name}`,
        url: uploadResult.url,
        name: uploadResult.name,
        alt: "",
        path,
        width: imageMeta.naturalWidth,
        height: imageMeta.naturalHeight,
        size: file.size,
        type: uploadResult.type,
        date: new Date().toISOString(),
      };
    }
    if (file.type.startsWith("video/")) {
      // Process videos concurrently
      const videoMetaPromise = new Promise<HTMLVideoElement>((resolve) => {
        const video = document.createElement("video");
        video.onloadedmetadata = () => resolve(video);
        video.src = URL.createObjectURL(file); // Use original file for metadata
      });
      const uploadPromise = handleFileUpload(file, path, {
        compressionOptions: {
          maxSizeMB: options?.compressionOptions?.maxSizeMB || 50,
          maxWidthOrHeight:
            options?.compressionOptions?.maxWidthOrHeight || 1920,
          useWebWorker: true,
        },
      });
      const [uploadResult, videoMeta] = await Promise.all([
        uploadPromise,
        videoMetaPromise,
      ]);

      return {
        url: uploadResult.url,
        name: uploadResult.name,
        path,
        width: videoMeta.videoWidth,
        height: videoMeta.videoHeight,
        size: file.size,
        type: uploadResult.type,
        date: new Date().toISOString(),
      };
    }
    if (file.type === "application/pdf") {
      const uploadResult = await handleFileUpload(file, path, {
        metadata: options?.metadata,
        compressionOptions: {
          maxSizeMB: options?.compressionOptions?.maxSizeMB || 50,
          useWebWorker: true,
        },
      });

      return {
        id: `${path}/${uploadResult.name}`,
        url: uploadResult.url,
        name: uploadResult.name,
        path,
        size: file.size,
        type: uploadResult.type,
        date: new Date().toISOString(),
      };
    }
    // Fallback for other types - let handleFileUpload manage naming
    console.warn(
      `Potentially unsupported file type encountered: ${file.type}. Uploading with default handling.`,
    );
    const uploadResult = await handleFileUpload(file, path, {
      metadata: options?.metadata,
    });

    return {
      id: `${path}/${uploadResult.name}`,
      url: uploadResult.url,
      name: uploadResult.name,
      path,
      size: file.size,
      type: uploadResult.type,
      date: new Date().toISOString(),
    };
  };

  const processedFilesPromise = Promise.all(
    fileList.map((file) => processFile(file)),
  );

  processedFilesPromise.then((files) => {
    const prepareFiles = files.map((e) => ({
      ...e,
      id: `${e.path}/${e.name}`,
      path: `${e.path}/${e.name}`,
      thumbnail: e.url,
    }));
    if (options?.addToCache !== false && overlappingKey) {
      addFilesToCacheRaw(overlappingKey, prepareFiles, sortMediaBrowserFiles);
    }
  });

  return processedFilesPromise;
};
